import { ReactNode, useState } from 'react';
import { Field, useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';
import { Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { GripVertical } from 'react-bootstrap-icons';
import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ButtonFormikTab } from '../../../../../components';
import { InfoIcon } from '../../../../../assets/icons/InfoIcon';
import { boolOption } from '../../../../../constant/setting';
import { ClassFormatEnum } from '../../../../../types/enums';
import { TournamentCategory } from '../../../../../types/tournament';
import {
  getBestOfNWinsNeeded,
  getMatchupBestOfN,
} from '../../../../../utils/bracketCellMatchupSummary.util';
import { getTeamCategoryDisplayLabelKey } from '../../../../../utils/tournamentCategoryTemplate';
import {
  applyTeamMatchupSlotAdd,
  applyTeamMatchupSlotChange,
  applyTeamMatchupSlotRemove,
  applyTeamMatchupSlotReorder,
  countActiveCategoryRubbers,
  expandTeamMatchupSlots,
  shouldOfferTiebreakerForClass,
  TeamRecipeCountChangeResult,
} from '../../../../../utils/teamCategoryRubberCount';
import { getBool } from '../../../../../utils/typeConverters';

type TeamCategoryRecipeTableProps = {
  categories: TournamentCategory[];
  parentClassDisabled: boolean;
  prefix: string;
  format: ClassFormatEnum | number;
  groupTiebreakerEnabled?: boolean;
  eliminationTiebreakerEnabled?: boolean;
};

type TeamRecipeFormValues = {
  tournamentCategories: TournamentCategory[];
  tiebreakerEnabled?: boolean;
  groupTiebreakerEnabled?: boolean;
  teamMatchupSlotOrder?: number[] | null;
};

const GROUP_STAGE_FORMATS = new Set<number>([
  ClassFormatEnum.GROUP,
  ClassFormatEnum.GROUP_ONLY,
  ClassFormatEnum.GROUP_BRACKET,
  ClassFormatEnum.BOX_LEAGUE,
  ClassFormatEnum.BOX_LEAGUE_ELIM,
]);

const KNOCKOUT_STAGE_FORMATS = new Set<number>([
  ClassFormatEnum.GROUP,
  ClassFormatEnum.GROUP_BRACKET,
  ClassFormatEnum.BRACKET,
  ClassFormatEnum.SINGLE_BRACKET,
]);

export type TeamRecipeProgressionLine = {
  key: string;
  n: number;
};

export type TeamRecipeProgressionCopy = {
  group?: TeamRecipeProgressionLine;
  knockout?: TeamRecipeProgressionLine;
};

/** Recipe footer copy: groups play all category matches; knockout Best of N includes the decider. */
export function getTeamRecipeProgressionCopy(input: {
  format: ClassFormatEnum | number;
  categoryCount: number;
  groupTiebreakerEnabled?: boolean;
  eliminationTiebreakerEnabled?: boolean;
}): TeamRecipeProgressionCopy {
  const format = Number(input.format);
  const groupN = input.categoryCount;
  const knockoutN = getMatchupBestOfN(input.categoryCount, !!input.eliminationTiebreakerEnabled);
  const hasGroup = GROUP_STAGE_FORMATS.has(format);
  const hasKnockout = KNOCKOUT_STAGE_FORMATS.has(format);
  const copy: TeamRecipeProgressionCopy = {};

  if (hasGroup) {
    copy.group = {
      key: input.groupTiebreakerEnabled
        ? 'bracketCellSummaryPlayAllDecider'
        : 'bracketCellSummaryPlayAll',
      n: groupN,
    };
  }
  if (hasKnockout || !hasGroup) {
    copy.knockout = {
      key: 'bracketCellProgressionBestOfN',
      n: knockoutN,
    };
  }

  return copy;
}

function showsGroupDecider(format: ClassFormatEnum | number): boolean {
  const n = Number(format);
  return n === ClassFormatEnum.GROUP || n === ClassFormatEnum.GROUP_ONLY;
}

function showsEliminationDecider(format: ClassFormatEnum | number): boolean {
  const n = Number(format);
  return n === ClassFormatEnum.GROUP || n === ClassFormatEnum.SINGLE_BRACKET;
}

function InfoTip({ text }: { text: string }) {
  return (
    <OverlayTrigger placement="top" overlay={<Tooltip>{text}</Tooltip>}>
      <Button type="button" variant="link" className="p-0 lh-1" aria-label={text}>
        <InfoIcon width={16} height={16} />
      </Button>
    </OverlayTrigger>
  );
}

function SortableRecipeRow({
  id,
  disabled,
  dragLabel,
  children,
}: {
  id: string;
  disabled: boolean;
  dragLabel: string;
  children: ReactNode;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    disabled,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };

  return (
    <tr ref={setNodeRef} style={style} data-testid={`recipe-slot-${id}`}>
      <td className="classes-tag__scorecard-num">
        <button
          type="button"
          className="classes-tag__scorecard-grip"
          aria-label={dragLabel}
          disabled={disabled}
          {...attributes}
          {...listeners}
        >
          <GripVertical />
        </button>
      </td>
      {children}
    </tr>
  );
}

type PickerTarget = number | 'add';

function MatchTypePicker({
  categories,
  prefix,
  onPick,
  onCancel,
}: {
  categories: TournamentCategory[];
  prefix: string;
  onPick: (categoryIndex: number) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="classes-tag__scorecard-picker" data-testid="match-type-picker">
      <div className="small text-muted mb-2">{t(prefix + 'bracketCellPickMatchType')}</div>
      <div className="d-flex flex-wrap gap-2">
        {categories.map((category, index) => (
          <Button
            key={category.id ?? index}
            type="button"
            variant="outline-dark"
            size="sm"
            onClick={() => onPick(index)}
          >
            {t(getTeamCategoryDisplayLabelKey(category))}
          </Button>
        ))}
      </div>
      <Button type="button" variant="link" size="sm" className="ps-0 mt-1" onClick={onCancel}>
        {t('modal-cancel')}
      </Button>
    </div>
  );
}

export function TeamCategoryRecipeTable({
  categories,
  parentClassDisabled,
  prefix,
  format,
  groupTiebreakerEnabled = false,
  eliminationTiebreakerEnabled = false,
}: TeamCategoryRecipeTableProps) {
  const { t } = useTranslation();
  const { setValues, values } = useFormikContext<TeamRecipeFormValues>();
  const [picker, setPicker] = useState<PickerTarget | null>(null);
  const slots = expandTeamMatchupSlots(categories, values.teamMatchupSlotOrder);
  const slotIds = slots.map((_, index) => `slot-${index}`);
  const total = countActiveCategoryRubbers(categories);
  const canOfferTiebreaker = shouldOfferTiebreakerForClass(categories);
  const showGroupDecider = showsGroupDecider(format);
  const showElimDecider = showsEliminationDecider(format);
  const showDeciderRow = canOfferTiebreaker && (showGroupDecider || showElimDecider);
  const progression = getTeamRecipeProgressionCopy({
    format,
    categoryCount: total,
    groupTiebreakerEnabled,
    eliminationTiebreakerEnabled,
  });
  const flags = {
    tiebreakerEnabled: eliminationTiebreakerEnabled,
    groupTiebreakerEnabled,
  };
  const tiedScore = total / 2;
  const sensors = useSensors(
    useSensor(MouseSensor),
    useSensor(TouchSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const commit = (patch: TeamRecipeCountChangeResult<TournamentCategory>) => {
    setValues({
      ...values,
      ...patch,
    });
    setPicker(null);
  };

  const addMatch = (categoryIndex: number) => {
    commit(applyTeamMatchupSlotAdd(categories, categoryIndex, flags, values.teamMatchupSlotOrder));
  };

  const removeMatch = (slotIndex: number) => {
    commit(applyTeamMatchupSlotRemove(categories, slotIndex, flags, values.teamMatchupSlotOrder));
  };

  const changeMatch = (slotIndex: number, toIndex: number) => {
    commit(
      applyTeamMatchupSlotChange(
        categories,
        slotIndex,
        toIndex,
        flags,
        values.teamMatchupSlotOrder
      )
    );
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) {
      return;
    }
    const fromIndex = slotIds.indexOf(String(active.id));
    const toIndex = slotIds.indexOf(String(over.id));
    if (fromIndex === -1 || toIndex === -1) {
      return;
    }
    commit(
      applyTeamMatchupSlotReorder(
        categories,
        fromIndex,
        toIndex,
        flags,
        values.teamMatchupSlotOrder
      )
    );
  };

  return (
    <div className="classes-tag__scorecard">
      {categories.map((category, index) =>
        category.categoryTemplate != null ? (
          <span key={`hidden-${category.id ?? index}`}>
            <Field type="hidden" name={`tournamentCategories.${index}.categoryTemplate`} />
            <Field type="hidden" name={`tournamentCategories.${index}.matchPlayerFormat`} />
            <Field type="hidden" name={`tournamentCategories.${index}.gender`} />
            <Field type="hidden" name={`tournamentCategories.${index}.numberOfMatches`} />
          </span>
        ) : (
          <Field
            key={`hidden-count-${category.id ?? index}`}
            type="hidden"
            name={`tournamentCategories.${index}.numberOfMatches`}
          />
        )
      )}

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
      <table className="classes-tag__scorecard-table">
        <thead>
          <tr>
            <th scope="col" className="classes-tag__scorecard-num">
              <span className="visually-hidden">{t(prefix + 'categoriesDragHandle')}</span>
            </th>
            <th scope="col">{t(prefix + 'categoriesMatchColumn')}</th>
            <th scope="col" className="classes-tag__scorecard-actions">
              <span className="visually-hidden">{t('button-change')}</span>
            </th>
          </tr>
        </thead>
        <tbody>
            <SortableContext items={slotIds} strategy={verticalListSortingStrategy}>
              {slots.map((slot, index) => {
                const category = categories[slot.categoryIndex];
                const label = t(getTeamCategoryDisplayLabelKey(category));
                const isEditing = picker === index;
                const slotId = slotIds[index];

                return (
                  <SortableRecipeRow
                    key={slotId}
                    id={slotId}
                    disabled={parentClassDisabled}
                    dragLabel={t(prefix + 'categoriesDragHandle')}
                  >
                    <th scope="row">
                      <div className="classes-tag__scorecard-match">
                        <span className="classes-tag__scorecard-index">{index + 1}</span>
                        <span>{label}</span>
                      </div>
                      {isEditing && !parentClassDisabled ? (
                        <MatchTypePicker
                          categories={categories}
                          prefix={prefix}
                          onPick={(toIndex) => changeMatch(index, toIndex)}
                          onCancel={() => setPicker(null)}
                        />
                      ) : null}
                    </th>
                    <td className="classes-tag__scorecard-actions">
                      <Button
                        type="button"
                        variant="link"
                        size="sm"
                        className="p-0"
                        disabled={parentClassDisabled}
                        onClick={() => setPicker(isEditing ? null : index)}
                      >
                        {t('button-change')}
                      </Button>
                      <Button
                        type="button"
                        variant="link"
                        size="sm"
                        className="p-0 text-dark"
                        disabled={parentClassDisabled}
                        aria-label={t('button-removeMatch')}
                        onClick={() => removeMatch(index)}
                      >
                        {t('button-removeMatch')}
                      </Button>
                    </td>
                  </SortableRecipeRow>
                );
              })}
            </SortableContext>
          {showDeciderRow ? (
            <tr className="classes-tag__scorecard-decider">
              <td colSpan={3}>
                <div className="classes-tag__scorecard-match">
                  <span className="classes-tag__scorecard-index">+</span>
                  <span>{t(prefix + 'categoriesDeciderIfTied')}</span>
                  <InfoTip text={t(prefix + 'categoriesDeciderNotScheduled')} />
                </div>
                <div className="classes-tag__scorecard-decider-controls">
                  {showGroupDecider ? (
                    <div className="classes-tag__scorecard-decider-control">
                      <div className="small mb-1 d-flex align-items-center gap-2 flex-wrap">
                        <span>{t(prefix + 'categoriesProgressionGroups')}</span>
                        <InfoTip text={t(prefix + 'deciderMatchVsRankingHint')} />
                      </div>
                      <ButtonFormikTab
                        currValue={String(getBool(values.groupTiebreakerEnabled))}
                        items={boolOption}
                        name="groupTiebreakerEnabled"
                        disabled={parentClassDisabled}
                      />
                      <div className="small text-muted mt-1">
                        {t(prefix + 'groupTiebreakerOffHint', { tied: tiedScore })}
                      </div>
                    </div>
                  ) : null}
                  {showElimDecider ? (
                    <div className="classes-tag__scorecard-decider-control">
                      <div className="small mb-1 d-flex align-items-center gap-2 flex-wrap">
                        <span>{t(prefix + 'categoriesProgressionKnockout')}</span>
                        <span className="badge bg-success">
                          {t(prefix + 'tiebreakerRecommended')}
                        </span>
                        <InfoTip text={t(prefix + 'tiebreakerEvenRubberHint', { count: total })} />
                      </div>
                      <ButtonFormikTab
                        currValue={String(getBool(values.tiebreakerEnabled))}
                        items={boolOption}
                        name="tiebreakerEnabled"
                        disabled={parentClassDisabled}
                      />
                    </div>
                  ) : null}
                </div>
              </td>
            </tr>
          ) : null}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={3} className="classes-tag__scorecard-footer">
              <div>{t(prefix + 'categoriesTotal', { count: total })}</div>
              {total > 0 && progression.group ? (
                <div
                  className="classes-tag__scorecard-footer-line"
                  data-testid="recipe-progression-group"
                >
                  <span className="classes-tag__scorecard-footer-stage">
                    {t(prefix + 'categoriesProgressionGroups')}
                  </span>
                  <span>{t(prefix + progression.group.key, { n: progression.group.n })}</span>
                </div>
              ) : null}
              {total > 0 && progression.knockout ? (
                <div
                  className="classes-tag__scorecard-footer-line"
                  data-testid="recipe-progression-knockout"
                >
                  <span className="classes-tag__scorecard-footer-stage">
                    {t(prefix + 'categoriesProgressionKnockout')}
                  </span>
                  <span>{t(prefix + progression.knockout.key, { n: progression.knockout.n })}</span>
                  {progression.knockout.n > total ? (
                    <InfoTip
                      text={t(prefix + 'bracketCellProgressionBestOfNHint', {
                        winsNeeded: getBestOfNWinsNeeded(total),
                      })}
                    />
                  ) : null}
                </div>
              ) : null}
            </td>
          </tr>
        </tfoot>
      </table>
      </DndContext>

      <div className="mt-3">
        {picker === 'add' && !parentClassDisabled ? (
          <MatchTypePicker
            categories={categories}
            prefix={prefix}
            onPick={addMatch}
            onCancel={() => setPicker(null)}
          />
        ) : (
          <Button
            type="button"
            variant="outline-dark"
            size="sm"
            disabled={parentClassDisabled}
            onClick={() => setPicker('add')}
          >
            {t('button-addMatch')}
          </Button>
        )}
      </div>
      <p className="small text-muted mt-2 mb-0" data-testid="recipe-match-order-hint">
        {t(prefix + 'categoriesMatchOrderHint')}
      </p>
    </div>
  );
}
